import { useEffect, useMemo, useState } from 'react';
import { Plus, Truck } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { countVehicleUsage, deleteVehicle, returnVehicleToBase } from '../../../db/fleet-store';
import {
  EMPTY_VEHICLE_FILTERS,
  VEHICLE_SCOPES,
  VEHICLE_SCOPE_LABELS,
  filterVehicles,
  isVehicleIdHit,
  parseFleetSearch,
  vehicleMaintenanceCount,
  vehicleScopeCounts,
} from '../../../core/fleet-filters';
import type { VehicleFilters } from '../../../core/fleet-filters';
import {
  EMPTY_ASSIGNMENT_INDEX,
  effectiveVehicleStatus,
  isVehicleOnActiveTask,
} from '../../../core/effective-status';
import type { AssignmentIndex } from '../../../core/effective-status';
import { hasPermission } from '../../../core/permissions';
import type { Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { VehicleCard } from './VehicleCard';
import { VehicleDetailsModal } from './VehicleDetailsModal';
import { VehicleFilterBar } from './VehicleFilterBar';
import { VehicleFormModal } from './VehicleFormModal';
import { VehicleTransferModal } from './VehicleTransferModal';
import type { Soldier } from '../../../types/fleet';

interface VehiclesPageProps {
  user: User;
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  /**
   * Which vehicles are out on an active task right now. The pool reads its
   * statuses through it, so a vehicle bound to a convoy is "במשימה" here
   * without its record having been touched.
   */
  assignments?: AssignmentIndex;
  loading: boolean;
  reload: () => Promise<void>;
}

/** Which vehicle the form is open for: an existing record, or a blank one. */
type FormTarget = { mode: 'create' } | { mode: 'edit'; vehicle: Vehicle };

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-panel border border-line rounded-2xl p-3 text-center">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-ink/50">{label}</p>
    </div>
  );
}

/** The vehicle roster: counters, status tabs, search and the card grid. */
export function VehiclesPage({
  user,
  vehicles,
  soldiers,
  assignments = EMPTY_ASSIGNMENT_INDEX,
  loading,
  reload,
}: VehiclesPageProps) {
  const [filters, setFilters] = useState<VehicleFilters>(EMPTY_VEHICLE_FILTERS);
  const [selected, setSelected] = useState<Vehicle | null>(null);
  const [form, setForm] = useState<FormTarget | null>(null);
  /** The vehicle the delete confirmation is open for. */
  const [pendingDelete, setPendingDelete] = useState<Vehicle | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** How many convoys still hold it — read as the confirmation opens. */
  const [usage, setUsage] = useState<number | null>(null);
  /** The vehicle the company sheet is open for — from the card or the badge. */
  const [transfer, setTransfer] = useState<Vehicle | null>(null);

  const canManage = hasPermission(user, 'MANAGE_VEHICLES');
  const scopeCounts = useMemo(() => vehicleScopeCounts(vehicles), [vehicles]);
  const inMaintenance = useMemo(() => vehicleMaintenanceCount(vehicles), [vehicles]);
  const visible = useMemo(
    () => filterVehicles(vehicles, filters, assignments),
    [vehicles, filters, assignments],
  );
  /** How many of the pool are actually out — the counter above the grid. */
  const onMission = useMemo(
    () => vehicles.filter((vehicle) => effectiveVehicleStatus(vehicle, assignments) === 'in_use')
      .length,
    [vehicles, assignments],
  );
  const query = useMemo(() => parseFleetSearch(filters.text), [filters.text]);

  // The convoy count is read once per confirmation, so the warning is on the
  // screen before the delete is attempted rather than after it is refused.
  useEffect(() => {
    if (!pendingDelete) {
      setUsage(null);
      return;
    }
    let live = true;
    void countVehicleUsage(pendingDelete.id).then((count) => {
      if (live) setUsage(count);
    });
    return () => {
      live = false;
    };
  }, [pendingDelete]);

  const closeConfirm = () => {
    setPendingDelete(null);
    setDeleteError(null);
  };

  // "החזר לשינוע" is a one-touch action, so it writes and re-reads without a
  // dialog. The store decides what the move does to the status.
  const handleReturn = async (vehicle: Vehicle) => {
    await returnVehicleToBase(user, vehicle.id);
    if (selected?.id === vehicle.id) setSelected(null);
    await reload();
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteVehicle(user, pendingDelete.id);
      // A vehicle that was open on screen is no longer in the roster.
      if (selected?.id === pendingDelete.id) setSelected(null);
      setPendingDelete(null);
      await reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Truck size={20} className="text-brand-500" aria-hidden />
          רכבים
        </h2>
        {canManage ? (
          <button
            type="button"
            onClick={() => setForm({ mode: 'create' })}
            className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center gap-2"
          >
            <Plus size={16} aria-hidden />
            רכב חדש
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="סה״כ רכבים" value={scopeCounts.all} />
        <StatTile label="זמינים בשינוע" value={scopeCounts.home} />
        <StatTile label="במשימה כעת" value={onMission} />
        <StatTile label="בטיפול / תקול" value={inMaintenance} />
      </div>

      <nav aria-label="סינון מצבת הרכבים" className="flex gap-1.5 overflow-x-auto pb-1">
        {VEHICLE_SCOPES.map((scope) => {
          const isActive = filters.scope === scope;
          return (
            <button
              key={scope}
              type="button"
              onClick={() => setFilters({ ...filters, scope })}
              aria-current={isActive ? 'true' : undefined}
              className={[
                'shrink-0 h-9 px-3 rounded-xl border text-sm transition-colors',
                isActive
                  ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold'
                  : 'border-line bg-panel text-ink/60 hover:text-ink',
              ].join(' ')}
            >
              {VEHICLE_SCOPE_LABELS[scope]}
              <span className="mr-1.5 text-xs text-ink/40 tabular-nums">{scopeCounts[scope]}</span>
            </button>
          );
        })}
      </nav>

      <VehicleFilterBar filters={filters} onChange={setFilters} resultCount={visible.length} />

      {loading ? (
        <p className="text-sm text-ink/50 py-8 text-center">טוען את מצבת הרכבים…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-ink/50 py-8 text-center">
          לא נמצאו רכבים התואמים את החיפוש{filters.text ? ` "${filters.text}"` : ''}.
        </p>
      ) : (
        // The rows are short enough that two or three columns fit the pool on
        // one screen; every detail and every transfer is one tap away in the
        // vehicle card.
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              onMission={isVehicleOnActiveTask(vehicle.id, assignments)}
              highlighted={isVehicleIdHit(vehicle, query)}
              onOpen={setSelected}
              onEdit={canManage ? (target) => setForm({ mode: 'edit', vehicle: target }) : undefined}
              onQuickStatus={canManage ? (target) => setTransfer(target) : undefined}
            />
          ))}
        </div>
      )}

      {selected ? (
        <VehicleDetailsModal
          vehicle={selected}
          soldiers={soldiers}
          user={user}
          onClose={() => setSelected(null)}
          onEdit={(vehicle) => {
            setSelected(null);
            setForm({ mode: 'edit', vehicle });
          }}
          onDelete={canManage ? (vehicle) => setPendingDelete(vehicle) : undefined}
          onReturnToBase={canManage ? (vehicle) => void handleReturn(vehicle) : undefined}
          onLoan={
            canManage
              ? (vehicle) => {
                  setSelected(null);
                  setTransfer(vehicle);
                }
              : undefined
          }
        />
      ) : null}

      {transfer ? (
        <VehicleTransferModal
          user={user}
          vehicle={transfer}
          vehicles={vehicles}
          onClose={() => setTransfer(null)}
          onSaved={reload}
        />
      ) : null}

      {form ? (
        <VehicleFormModal
          user={user}
          vehicle={form.mode === 'edit' ? form.vehicle : null}
          onClose={() => setForm(null)}
          onSaved={reload}
        />
      ) : null}

      {pendingDelete ? (
        <Modal
          title="מחיקת רכב"
          subtitle={`${pendingDelete.militaryId || pendingDelete.plateNumber} · ${pendingDelete.name || pendingDelete.model}`}
          onClose={closeConfirm}
          footer={
            <>
              <button
                type="button"
                onClick={closeConfirm}
                className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={deleting || usage !== 0}
                onClick={() => void handleDelete()}
                className="h-10 px-5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                {deleting ? 'מוחק…' : 'מחק'}
              </button>
            </>
          }
        >
          <p className="text-sm">האם אתה בטוח שברצונך למחוק רכב זה?</p>
          <p className="mt-2 text-xs text-ink/50">
            הרכב יוסר לצמיתות ממצבת הרכבים, יחד עם כל החרגה שנרשמה עליו. לא ניתן לשחזר פעולה זו.
          </p>
          {usage === null ? (
            <p className="mt-3 text-xs text-ink/50">בודק שיבוצים קיימים…</p>
          ) : usage > 0 ? (
            <p className="mt-3 text-sm rounded-xl border border-amber-200 bg-amber-50 text-amber-800 p-3">
              הרכב משובץ ל־{usage} משימות. יש להסיר אותו מהשיבוצים לפני המחיקה.
            </p>
          ) : null}
          {deleteError ? (
            <p className="mt-3 text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
              {deleteError}
            </p>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
